import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageShell from '../components/PageShell.jsx';
import LeadForm from '../components/LeadForm.jsx';
import CourseCurriculum from '../components/CourseCurriculum.jsx';
import ProgramCard from '../components/ProgramCard.jsx';
import Seo from '../components/Seo.jsx';
import { useLeadModal } from '../components/LeadModalProvider.jsx';
import CompareButton from '../components/CompareButton.jsx';
import EmiCalculator from '../components/EmiCalculator.jsx';
import {
  fillTemplate,
  getCourseCopy,
  getLeadIntentCopy,
  getSite,
  programInterestLabel,
  resolveCourseSeo
} from '../content/index.js';
import { formatDurationMonths, formatMoney } from '../utils/format.js';

export default function CoursePage() {
  const { universitySlug, courseSlug } = useParams();
  const copy = getCourseCopy();
  const site = getSite();
  const feesCopy = getLeadIntentCopy('fees');
  const brochureCopy = getLeadIntentCopy('brochure');
  const { openLeadModal } = useLeadModal();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    async function load() {
      setState({ loading: true, error: null, data: null });
      try {
        const res = await fetch(`/api/universities/${universitySlug}/courses/${courseSlug}`);
        if (res.status === 404) {
          setState({ loading: false, error: 'Course not found.', data: null });
          return;
        }
        if (!res.ok) throw new Error('Failed');
        const payload = await res.json();
        setState({ loading: false, error: null, data: payload.data });
      } catch {
        setState({ loading: false, error: 'Unable to load course details.', data: null });
      }
    }

    load();
  }, [universitySlug, courseSlug]);

  const {
    course,
    university,
    hero,
    curriculum,
    fees,
    highlights,
    admissionSteps,
    careerPaths,
    relatedCourses
  } = state.data || {};

  const seo = useMemo(
    () => resolveCourseSeo(course?.name, university?.name),
    [course?.name, university?.name]
  );

  const preferredProgram = programInterestLabel(course?.name, university?.name);
  const feesLeadSource = `course_fees_${courseSlug}`;

  function handleGetFees(event) {
    event.preventDefault();
    event.stopPropagation();
    openLeadModal({
      intent: 'fees',
      leadSource: feesLeadSource,
      preferredProgram
    });
  }

  function handleBrochure(event) {
    event.preventDefault();
    event.stopPropagation();
    openLeadModal({
      intent: 'brochure',
      leadSource: `brochure_${courseSlug}`,
      preferredProgram,
      brochureAvailable: Boolean(course?.hasBrochure),
      extraPayload: {
        brochureCourseSlug: courseSlug,
        notes: course?.hasBrochure ? 'Intent: brochure download' : 'Intent: brochure request'
      }
    });
  }

  return (
    <PageShell>
      {course && university && (
        <Seo title={seo.title} description={seo.description} />
      )}

      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to={`/universities/${universitySlug}`}>{university?.name || 'University'}</Link>
          <span>/</span>
          <span>{course?.name || 'Course'}</span>
        </div>
      </div>

      {state.loading && <p className="status-text container">Loading course details...</p>}
      {state.error && <p className="status-text error container">{state.error}</p>}

      {state.data && (
        <div className="course-landing">
          <section className="course-hero">
            <div className="container course-hero-panel">
              <div className="course-hero-main">
                <div className="badge-row">
                  {hero.badges.map((badge) => (
                    <span className="badge" key={badge}>
                      {badge}
                    </span>
                  ))}
                </div>
                <h1>
                  {hero.title}
                  <br />
                  <span className="course-hero-uni">{hero.titleLine2}</span>
                </h1>

                <div className="course-quick-facts">
                  <div>
                    <strong>Duration</strong>
                    <span>{hero.durationLabel}</span>
                  </div>
                  {hero.semesterFeeLabel && (
                    <div>
                      <strong>Fees</strong>
                      <span>{hero.semesterFeeLabel}</span>
                    </div>
                  )}
                  <div>
                    <strong>Eligibility</strong>
                    <span>{hero.eligibility}</span>
                  </div>
                </div>

                <div className="course-hero-actions">
                  <button
                    type="button"
                    className="cta-button lead-intent-btn"
                    data-lead-intent="fees"
                    data-lead-source={feesLeadSource}
                    data-lead-program={preferredProgram}
                    onClick={handleGetFees}
                  >
                    {feesCopy.cta || copy.ctaButton}
                  </button>
                  <button
                    type="button"
                    className="cta-button cta-button-secondary brochure-btn"
                    data-lead-intent="brochure"
                    onClick={handleBrochure}
                  >
                    {course.hasBrochure ? brochureCopy.cta : brochureCopy.ctaRequest}
                  </button>
                  <CompareButton
                    className="compare-toggle-hero"
                    course={{
                      slug: course.slug,
                      name: course.name,
                      universityName: university.name,
                      universitySlug,
                      levelCode: course.levelCode
                    }}
                  />
                </div>
              </div>

              <aside className="course-hero-aside">
                <LeadForm
                  key={feesLeadSource}
                  title={copy.leadTitle}
                  subtitle={copy.leadSubtitle}
                  leadSource={feesLeadSource}
                  submitLabel={feesCopy.submit}
                  intent="fees"
                  defaultProgram={preferredProgram}
                />
              </aside>
            </div>
          </section>

          <section className="section">
            <div className="container">
              <header className="section-header">
                <h2>{copy.feeHeading}</h2>
                <p>{fees.note}</p>
              </header>
              <div className="fee-grid">
                {fees.fullCourseFee !== null && (
                  <article className="fee-card fee-card-primary">
                    <span>Full course fee</span>
                    {fees.offerFullCourseFee !== null &&
                    fees.offerFullCourseFee < fees.fullCourseFee ? (
                      <>
                        <strong className="fee-strike">
                          {formatMoney(fees.fullCourseFee, fees.currency)}
                        </strong>
                        <strong>{formatMoney(fees.offerFullCourseFee, fees.currency)}</strong>
                      </>
                    ) : (
                      <strong>{formatMoney(fees.fullCourseFee, fees.currency)}</strong>
                    )}
                  </article>
                )}
                {fees.semesterFee !== null && (
                  <article className="fee-card">
                    <span>Each semester fee</span>
                    <strong>{formatMoney(fees.semesterFee, fees.currency)}</strong>
                  </article>
                )}
                {fees.annualFee !== null && (
                  <article className="fee-card">
                    <span>Annual fee</span>
                    <strong>{formatMoney(fees.annualFee, fees.currency)}</strong>
                  </article>
                )}
                {fees.emiPerMonth !== null && (
                  <article className="fee-card">
                    <span>EMI starting at</span>
                    <strong>{formatMoney(fees.emiPerMonth, fees.currency)} / month</strong>
                  </article>
                )}
              </div>
              <div className="fee-email-cta">
                <p>{copy.feeEmailPrompt}</p>
                <button
                  type="button"
                  className="cta-button lead-intent-btn"
                  data-lead-intent="fees"
                  data-lead-source={feesLeadSource}
                  data-lead-program={preferredProgram}
                  onClick={handleGetFees}
                >
                  {feesCopy.cta || copy.ctaButton}
                </button>
              </div>
            </div>
          </section>

          {fees.fullCourseFee !== null && (
            <section className="section">
              <div className="container">
                <EmiCalculator
                  key={course.slug}
                  totalFee={fees.offerFullCourseFee ?? fees.fullCourseFee}
                  currency={fees.currency}
                  courseDurationMonths={course.durationMonths}
                  programName={preferredProgram}
                  leadSource={`emi_${courseSlug}`}
                />
              </div>
            </section>
          )}

          <section className="section">
            <div className="container">
              <header className="section-header">
                <h2>{copy.highlightsHeading}</h2>
              </header>
              <div className="highlights-grid">
                {highlights.map((item) => (
                  <article className="highlight-card" key={item.title}>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <div className="container">
            <CourseCurriculum
              curriculum={curriculum}
              hasBrochure={course.hasBrochure}
              onBrochure={handleBrochure}
              courseName={course.name}
            />
          </div>

          <section className="section">
            <div className="container">
              <header className="section-header">
                <h2>{copy.admissionHeading}</h2>
              </header>
              <ol className="admission-steps">
                {admissionSteps.map((step) => (
                  <li key={step.step}>
                    <span className="step-num">{step.step}</span>
                    <div>
                      <h3>{step.title}</h3>
                      <p>{step.description}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          <section className="section">
            <div className="container">
              <header className="section-header">
                <h2>{copy.careersHeading}</h2>
              </header>
              <div className="chips-row">
                {careerPaths.map((path) => (
                  <span className="chip" key={path}>
                    {path}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {relatedCourses?.length > 0 && (
            <section className="section">
              <div className="container">
                <header className="section-header">
                  <h2>
                    {fillTemplate(copy.relatedHeading, { university: university.name })}
                  </h2>
                </header>
                <div className="course-grid">
                  {relatedCourses.map((related) => (
                    <ProgramCard
                      key={related.id}
                      to={`/universities/${universitySlug}/courses/${related.slug}`}
                      slug={related.slug}
                      name={related.name}
                      universityName={university.name}
                      universitySlug={universitySlug}
                      levelCode={related.levelCode}
                      totalFeeMin={related.totalFeeMin}
                      currency={related.currency}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="section course-cta-band">
            <div className="container course-cta-inner">
              <div>
                <h2>{fillTemplate(copy.ctaHeading, { course: course.name })}</h2>
                <p>
                  {formatDurationMonths(course.durationMonths)} • {course.mode} •{' '}
                  {university.name}
                </p>
              </div>
              <button
                type="button"
                className="cta-button lead-intent-btn"
                data-lead-intent="fees"
                data-lead-source={feesLeadSource}
                data-lead-program={preferredProgram}
                onClick={handleGetFees}
              >
                {copy.ctaButton || feesCopy.cta || site.ctaCounsellor}
              </button>
            </div>
          </section>
        </div>
      )}
    </PageShell>
  );
}
